import React, { useState } from "react";
import {
  Filter,
  FilterOption,
  FilterTypeEnum,
} from "../../types/api/filters.type";
import { Select, SelectVariantEnum } from "../core/Select/Select";
import {
  FilterProps,
  OrganizationFilterPropertyEnum,
  TechSkillsFilterPropertyEnum,
} from "../../types/AppTypes";
import { FiltersTitles } from "../../constants/FilterTitles";
import {
  StyledFiltersContent,
  StyledFiltersList,
  StyledFiltersCollapse,
} from "./FiltersContent.styled";
import { Accordion, AccordionVariantEnum } from "../core/Accordion/Accordion";
import { ContainerChips } from "../ContainerChips/ContainerChips";
import { FilterUpdate } from "../FiltersPanel/FiltersPanel";
import {
  useOrganizationFilterOptions,
  useTechSkillsFilterOptions,
} from "../../hooks/filtersContent.hook";

type CustomSelectProps = {
  item: Filter;
  selectExist?: FilterProps;
  tmpFilters: FilterProps[];
  handleFilter: (filtersUpdates: FilterUpdate[], type: FilterTypeEnum) => void;
};

const getOptionValue = (options: FilterOption[], value: string | number) => {
  return options.find((option) => option.value === value)?.value;
};

const MultiSelectTechSkills = ({
  item,
  selectExist,
  tmpFilters,
  handleFilter,
}: CustomSelectProps) => {
  const queryParamsTechSkills = [
    ...(item.key === TechSkillsFilterPropertyEnum.COMPETENCE
      ? []
      : [
          {
            key: TechSkillsFilterPropertyEnum.COMPETENCE,
            value:
              tmpFilters.find(
                (filter) =>
                  filter.key === TechSkillsFilterPropertyEnum.COMPETENCE,
              )?.value ?? [],
          },
        ]),
    ...(item.key === TechSkillsFilterPropertyEnum.STREAM
      ? []
      : [
          {
            key: TechSkillsFilterPropertyEnum.STREAM,
            value:
              tmpFilters.find(
                (filter) => filter.key === TechSkillsFilterPropertyEnum.STREAM,
              )?.value ?? [],
          },
        ]),
    ...(item.key === TechSkillsFilterPropertyEnum.SOFTWARE
      ? []
      : [
          {
            key: TechSkillsFilterPropertyEnum.SOFTWARE,
            value:
              tmpFilters.find(
                (filter) =>
                  filter.key === TechSkillsFilterPropertyEnum.SOFTWARE,
              )?.value ?? [],
          },
        ]),
    ...(item.key === TechSkillsFilterPropertyEnum.COMPONENT
      ? []
      : [
          {
            key: TechSkillsFilterPropertyEnum.COMPONENT,
            value:
              tmpFilters.find(
                (filter) =>
                  filter.key === TechSkillsFilterPropertyEnum.COMPONENT,
              )?.value ?? [],
          },
        ]),
  ];

  const {
    data: infiniteData,
    initLoading,
    canLoadMore,
    fetchMore,
    setSearch,
    fetchingMore,
    search,
  } = useTechSkillsFilterOptions({
    item,
    queryParamsTechSkills,
  });

  return (
    <Select
      variant={SelectVariantEnum.MULTI_SELECT}
      onChange={(e) => {
        handleFilter([{ value: e, key: item.key }], item.type);
      }}
      label={item.label}
      options={infiniteData.map((item) => ({
        value: item,
        label: item,
      }))}
      {...(selectExist && {
        value: selectExist.value,
      })}
      dropdownFixed={false}
      loading={initLoading}
      withSearch={true}
      canLoadMore={canLoadMore}
      fetchMore={fetchMore}
      fetchingMore={fetchingMore}
      onSearch={setSearch}
      searchValue={search}
    />
  );
};

const SelectOrganization = ({
  item,
  selectExist,
  tmpFilters,
  handleFilter,
}: CustomSelectProps) => {
  const [searchValues, setSearchValues] = useState<Map<string, string>>(
    new Map(),
  );

  const handleSearch = (value: string, key: string) => {
    setSearchValues((prev) => new Map(prev).set(key, value));
  };

  const currentSearchValue = searchValues.get(item.key) || "";

  const isPracticeFilter = item.key === OrganizationFilterPropertyEnum.PRACTICE;

  const queryParamsOrganization = [
    ...(isPracticeFilter
      ? []
      : [
          {
            key: OrganizationFilterPropertyEnum.PRACTICE,
            value:
              tmpFilters.find(
                (filter) =>
                  filter.key === OrganizationFilterPropertyEnum.PRACTICE,
              )?.value ?? [],
          },
        ]),
  ];

  const { data, isFetching: loading } = useOrganizationFilterOptions({
    item,
    queryParamsOrganization,
  });

  const filteredOptions = data
    ? data.filter((option) =>
        option.label.toLowerCase().includes(currentSearchValue.toLowerCase()),
      )
    : [];

  return (
    <Select
      variant={SelectVariantEnum.SINGLE_SELECT}
      onChange={(e) => {
        handleFilter(
          [
            { value: e, key: item.key },
            ...(item.key === OrganizationFilterPropertyEnum.PRACTICE
              ? [{ value: null, key: OrganizationFilterPropertyEnum.CC }]
              : []),
          ],
          item.type,
        );
      }}
      label={item.label}
      options={filteredOptions}
      {...(selectExist && {
        value: getOptionValue(data ?? [], selectExist.value[0]),
      })}
      dropdownFixed={false}
      loading={loading}
      withSearch={data ? data.length > 5 : false}
      onSearch={(value) => handleSearch(value, item.key)}
      searchValue={currentSearchValue}
    />
  );
};

type FiltersContentProps<T> = {
  handleFilter: (filtersUpdates: FilterUpdate[], type: FilterTypeEnum) => void;
  tmpFilters: FilterProps[];
  filtersTitles?: FiltersTitles[];
  data: T;
  defaultExpanded?: boolean;
};

export const FiltersContent = <T,>({
  tmpFilters,
  handleFilter,
  filtersTitles,
  data,
  defaultExpanded,
}: FiltersContentProps<T>) => {
  const [searchValues, setSearchValues] = useState<Map<string, string>>(
    new Map(),
  );

  const handleSearch = (value: string, key: string) => {
    setSearchValues((prev) => new Map(prev).set(key, value));
  };

  const generateContent = (content: Filter[]) => {
    const FilterStructure = (item: Filter) => {
      const selectExist = tmpFilters.find((el) => el.key === item.key);
      const currentSearchValue = searchValues.get(item.key) || "";

      const filteredOptions = currentSearchValue
        ? item.options.filter((option) =>
            option.label
              .toLowerCase()
              .includes(currentSearchValue.toLowerCase()),
          )
        : item.options;

      const isFilterOfTechSkills =
        item.key === TechSkillsFilterPropertyEnum.COMPETENCE ||
        item.key === TechSkillsFilterPropertyEnum.STREAM ||
        item.key === TechSkillsFilterPropertyEnum.SOFTWARE ||
        item.key === TechSkillsFilterPropertyEnum.COMPONENT;

      const isFilterOfOrganization =
        item.key === OrganizationFilterPropertyEnum.PRACTICE ||
        item.key === OrganizationFilterPropertyEnum.CC;

      return {
        SELECT: isFilterOfOrganization ? (
          <SelectOrganization
            item={item}
            selectExist={selectExist}
            tmpFilters={tmpFilters}
            handleFilter={handleFilter}
          />
        ) : (
          <Select
            variant={SelectVariantEnum.SINGLE_SELECT}
            onChange={(e) =>
              handleFilter([{ value: e, key: item.key }], item.type)
            }
            label={item.label}
            options={filteredOptions}
            {...(selectExist && {
              value: getOptionValue(item.options, selectExist.value[0]),
            })}
            dropdownFixed={false}
            withSearch={item.options.length > 5}
            onSearch={(value) => handleSearch(value, item.key)}
            searchValue={searchValues.get(item.key) || ""}
          />
        ),
        CHIP: (
          <ContainerChips
            keyFilter={item.key}
            options={item.options}
            filters={tmpFilters}
            setSelectedChip={(e) =>
              handleFilter([{ value: e, key: item.key }], item.type)
            }
          />
        ),
        MULTISELECT: isFilterOfTechSkills ? (
          <MultiSelectTechSkills
            item={item}
            selectExist={selectExist}
            tmpFilters={tmpFilters}
            handleFilter={handleFilter}
          />
        ) : (
          <Select
            variant={SelectVariantEnum.MULTI_SELECT}
            onChange={(e) => {
              handleFilter([{ value: e, key: item.key }], item.type);
            }}
            label={item.label}
            options={filteredOptions}
            {...(selectExist && {
              value: selectExist.value,
            })}
            dropdownFixed={false}
            withSearch={item.options.length > 5}
            onSearch={(value) => handleSearch(value, item.key)}
            searchValue={searchValues.get(item.key) || ""}
          />
        ),
      };
    };
    return content.map((item) => (
      <React.Fragment key={item.key}>
        {FilterStructure(item)[item.type]}
      </React.Fragment>
    ));
  };

  return (
    <>
      {data &&
        (filtersTitles && filtersTitles.length > 0 ? (
          filtersTitles
            .filter((item) => {
              const content = (data[item.key as keyof T] as Filter[]) ?? [];
              return (content.length ?? 0) > 0;
            })
            .map((item, index, array) => {
              const content = (data[item.key as keyof T] as Filter[]) ?? [];
              const selectedCount = tmpFilters.filter((filter) =>
                content.some((contentItem) => contentItem.key === filter.key),
              ).length;
              const isLast = index === array.length - 1;
              return (
                <StyledFiltersCollapse key={item.key} $isLast={isLast}>
                  <Accordion
                    key={item.key}
                    label={item.label}
                    defaultOpen={defaultExpanded}
                    variant={AccordionVariantEnum.SECONDARY}
                    selectedCount={selectedCount}
                  >
                    <StyledFiltersList>
                      {generateContent(content)}
                    </StyledFiltersList>
                  </Accordion>
                </StyledFiltersCollapse>
              );
            })
        ) : (
          <StyledFiltersContent>
            <StyledFiltersList>
              {generateContent(data as Filter[])}
            </StyledFiltersList>
          </StyledFiltersContent>
        ))}
    </>
  );
};
